import React from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Icon as LegacyIcon } from '@ant-design/compatible';
// import { RouteComponentProps } from 'react-router-dom';
import { Button, Empty, Input, Table, Tag } from 'antd';
import {
  useGetAllVehiclesQuery,
  GetAllVehiclesQuery,
} from '../../graphql/datamodel.gen';
import { getClient } from '../../apollo';
import Highlighter from 'react-highlight-words';
import VehicleTypesDrawer from './VehicleTypesDrawer/VehicleTypesDrawer';
import VehicleBrandModelsDrawer from './VehicleBrandModelsDrawer/VehicleBrandModelsDrawer';
import { getTableScroll } from '../../utils/table';

interface IProps {}
interface IState {}

type Vehicles = GetAllVehiclesQuery['vehicles'];
type Vehicle = GetAllVehiclesQuery['vehicles'][number];

const Vehicles: React.FC<IProps> = (props: IProps) => {
  const {
    loading: loadingVehicles,
    data: dataVehicles,
    error: errorVehicles,
  } = useGetAllVehiclesQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const vehicles = dataVehicles?.vehicles || [];
  const loading = loadingVehicles;

  return <VehiclesView vehicles={vehicles} loading={loading} />;
};

interface IViewProps {
  vehicles: Vehicles;
  loading: boolean;
}

interface IViewState {
  searchValue?: string;
  searchedColumn: string;
  filteredInfo?: Object;
  sortedInfo?: Object;
  searchText?: string;
  vehicleTypesManagementDrawerVisible: boolean;
  vehicleBrandModelsManagementDrawerVisible: boolean;
}

class VehiclesView extends React.Component<IViewProps, IViewState> {
  state = {
    searchValue: undefined,
    searchedColumn: '',
    filteredInfo: undefined,
    sortedInfo: undefined,
    searchText: undefined,
    vehicleTypesManagementDrawerVisible: false,
    vehicleBrandModelsManagementDrawerVisible: false,
  };

  private searchInput: React.RefObject<Input>;

  private handleSearch = (
    selectedKeys: any[],
    confirm: () => void,
    dataIndex: string,
  ) => {
    confirm();
    this.setState({ searchText: selectedKeys[0], searchedColumn: dataIndex });
  };

  private handleReset = (clearFilters: () => void, confirm: () => void) => {
    clearFilters();
    confirm();
    this.setState({ searchText: undefined });
  };

  private handleChange = (pagination: any, filters: any, sorter: any) => {
    this.setState({
      filteredInfo: filters,
      sortedInfo: sorter,
    });
  };

  private clearAllFilters = () => {
    this.setState({
      filteredInfo: undefined,
      sortedInfo: undefined,
      searchText: '',
    });
  };

  private openVehicleTypeManagementDrawer = () => {
    this.setState({
      vehicleTypesManagementDrawerVisible: true,
    });
  };

  private closeVehicleTypeManagementDrawer = () => {
    this.setState({
      vehicleTypesManagementDrawerVisible: false,
    });
  };

  private openVehicleBrandModelsManagementDrawer = () => {
    this.setState({
      vehicleBrandModelsManagementDrawerVisible: true,
    });
  };

  private closeVehicleBrandModelsManagementDrawer = () => {
    this.setState({
      vehicleBrandModelsManagementDrawerVisible: false,
    });
  };

  render(): React.ReactNode {
    let { sortedInfo, filteredInfo } = this.state;

    const columns = [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={this.searchInput}
              placeholder={`Buscar por ID`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'id')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'id')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            translate={undefined}
            style={{ color: filtered ? '#1890ff' : undefined }}
          />
        ),
        onFilter: (value: string, record: Vehicle) => {
          return record.id
            ?.toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput?.current?.select());
          }
        },
        render: (_: string, vehicle: Vehicle) => {
          const id = vehicle.volcanoVehicleId || vehicle.id;
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={id}
            />
          );
        },
      },
      {
        title: 'Matrícula',
        dataIndex: 'licensePlate',
        key: 'licensePlate',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={this.searchInput}
              placeholder={`Buscar por matrícula`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'licensePlate')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() =>
                this.handleSearch(selectedKeys, confirm, 'licensePlate')
              }
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            translate={undefined}
            style={{ color: filtered ? '#1890ff' : undefined }}
          />
        ),
        onFilter: (value: string, record: Vehicle) => {
          return record.licensePlate
            ?.toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput?.current?.select());
          }
        },
        render: (_: string, vehicle: Vehicle) => {
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={vehicle.licensePlate}
            />
          );
        },
      },
      {
        title: 'Marca',
        dataIndex: 'brand',
        key: 'brand',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={this.searchInput}
              placeholder={`Buscar por marca`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'brand')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'brand')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            translate={undefined}
            style={{ color: filtered ? '#1890ff' : undefined }}
          />
        ),
        onFilter: (value: string, record: Vehicle) => {
          return record.brand
            ?.toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput?.current?.select());
          }
        },
        render: (_: string, vehicle: Vehicle) => {
          if (vehicle?.brand) {
            return (
              <Highlighter
                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                searchWords={[this.state.searchText]}
                autoEscape
                textToHighlight={vehicle.brand}
              />
            );
          }
        },
      },
      {
        title: 'Modelo',
        dataIndex: 'model',
        key: 'model',
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
        }: any): React.ReactNode => (
          <div style={{ padding: 8 }}>
            <Input
              ref={this.searchInput}
              placeholder={`Buscar por modelo`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                this.handleSearch(selectedKeys, confirm, 'model')
              }
              style={{ width: 188, marginBottom: 8, display: 'block' }}
            />
            <Button
              type="primary"
              onClick={() => this.handleSearch(selectedKeys, confirm, 'model')}
              icon={<SearchOutlined translate={undefined} />}
              size="small"
              style={{ width: 90, marginRight: 8 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => this.handleReset(clearFilters, confirm)}
              size="small"
              style={{ width: 90 }}
            >
              Limpiar
            </Button>
          </div>
        ),
        filterIcon: (filtered: any): React.ReactNode => (
          <SearchOutlined
            translate={undefined}
            style={{ color: filtered ? '#1890ff' : undefined }}
          />
        ),
        onFilter: (value: string, record: Vehicle) => {
          return record.model
            ?.toString()
            .toLowerCase()
            .includes(value.toLowerCase());
        },
        onFilterDropdownVisibleChange: (visible: any) => {
          if (visible) {
            setTimeout(() => this.searchInput?.current?.select());
          }
        },
        render: (_: string, vehicle: Vehicle) => {
          return (
            <Highlighter
              highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
              searchWords={[this.state.searchText]}
              autoEscape
              textToHighlight={vehicle.model}
            />
          );
        },
      },
      {
        title: 'Origen',
        dataIndex: 'origin',
        key: 'origin',
        filters: [
          {
            text: 'Propio',
            value: 'own',
          },
          {
            text: 'Colaborador',
            value: 'collaborator',
          },
        ],
        // filteredValue: (filteredInfo as any)?.type || null,
        onFilter: (filter: string, record: Vehicle) => {
          return record.origin === filter;
        },
        sorter: (a: Vehicle, b: Vehicle) => a.origin?.length - b.origin?.length,
        sortOrder:
          (sortedInfo as any)?.columnKey === 'type' &&
          (sortedInfo as any)?.order,
        render: (type: string, vehicle: Vehicle) => {
          if (vehicle.origin === 'collaborator')
            return (
              <Tag color={'magenta'} key={vehicle.origin}>
                {'COLABORADOR'}
              </Tag>
            );
          if (vehicle.origin === 'own')
            return (
              <Tag color={'blue'} key={vehicle.origin}>
                {'PROPIO'}
              </Tag>
            );
          return undefined;
        },
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        render: (status: string, vehicle: Vehicle) => {
          if (vehicle.status === 'active')
            return (
              <Tag color={'green'} key={vehicle.status}>
                {'ACTIVO'}
              </Tag>
            );
          if (vehicle.status === 'inactive')
            return (
              <Tag color={'volcano'} key={vehicle.status}>
                {'INACTIVO'}
              </Tag>
            );
          return <Tag key={vehicle.status}>{'SIN ESTADO'}</Tag>;
        },

        filters: [
          {
            text: 'Activo',
            value: 'active',
          },
          {
            text: 'Inactivo',
            value: 'inactive',
          },
        ],
        // filteredValue: (filteredInfo as any)?.status || null,
        onFilter: (filter: string, record: Vehicle) => {
          return record.status === filter;
        },
        sorter: (a: Vehicle, b: Vehicle) => a.status?.length - b.status?.length,
        sortOrder:
          (sortedInfo as any)?.columnKey === 'status' &&
          (sortedInfo as any)?.order,
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 155,
      },
    ];

    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            paddingBottom: '2em',
          }}
        >
          <Button
            icon={<LegacyIcon type={'close'} />}
            onClick={this.clearAllFilters}
          >
            Eliminar filtros
          </Button>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline',
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={this.openVehicleBrandModelsManagementDrawer}>
              Gestionar marcas y modelos
            </Button>
            <Button
              onClick={this.openVehicleTypeManagementDrawer}
              style={
                /**
                 *
                 */
                { marginLeft: '15px' }
              }
            >
              Ver tipos de vehículo
            </Button>
          </div>
        </div>
        <Table
          rowKey={(record: Vehicle) => record.id}
          locale={{
            emptyText: (
              <Empty
                description="No hay vehículos disponibles"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
          size={'middle'}
          pagination={{ pageSize: 25 }}
          dataSource={[...this.props.vehicles]}
          scroll={{ y: getTableScroll() }}
          columns={columns}
          onChange={this.handleChange}
        />
        <VehicleBrandModelsDrawer
          visible={this.state.vehicleBrandModelsManagementDrawerVisible}
          onClose={this.closeVehicleBrandModelsManagementDrawer}
        />
        <VehicleTypesDrawer
          visible={this.state.vehicleTypesManagementDrawerVisible}
          onClose={this.closeVehicleTypeManagementDrawer}
        />
      </>
    );
  }
}

export default Vehicles;
